import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Cpu, KeyRound, Check, Loader2, Trash2, ShieldCheck,
} from "lucide-react";
import {
  fetchModels, fetchSettings, setPreferredModel, saveApiKey, removeApiKey, fetchAiConsent, withdrawAiConsent,
} from "@/lib/api";


const PROVIDERS = [
  { id: "openai", label: "OpenAI (GPT)", placeholder: "sk-...", link: "https://platform.openai.com/api-keys" },
  { id: "anthropic", label: "Anthropic (Claude)", placeholder: "sk-ant-...", link: "https://console.anthropic.com/settings/keys" },
  // OpenRouter has no shared key: its models only work with the user's own key.
  { id: "openrouter", label: "OpenRouter", placeholder: "sk-or-...", link: "https://openrouter.ai/settings/keys", ownKeyOnly: true },
];

/**
 * @param {React.ReactNode} [trigger] Inline trigger. Omit it and drive the
 *   dialog with `open` / `onOpenChange`, which is what the dashboard account
 *   menu does.
 */
export default function SettingsModal({ siteId, isDemo, onConsentChange, trigger, model, onModelChange, open: openProp, onOpenChange }) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const setOpen = controlled ? (onOpenChange ?? (() => {})) : setUncontrolledOpen;
  const [models, setModels] = useState([]);
  const [keys, setKeys] = useState({});
  const [preferred, setPreferred] = useState(model || "claude");
  const [inputs, setInputs] = useState({ openai: "", anthropic: "", openrouter: "" });
  const [busy, setBusy] = useState("");
  const [consent, setConsent] = useState(null);
  const [consentError, setConsentError] = useState(false);
  useEffect(() => { let active = true; setConsent(null); setConsentError(false); if (open && siteId && !isDemo) fetchAiConsent(siteId).then(record => { if (active) setConsent(record); }).catch(() => { if (active) setConsentError(true); }); return () => { active = false; }; }, [open, siteId, isDemo]);
  const withdraw = async provider => { setBusy(provider); try { await withdrawAiConsent(siteId, provider); setConsent(await fetchAiConsent(siteId)); onConsentChange?.(); toast.success("AI sharing withdrawn"); } catch { toast.error("Withdrawal could not be confirmed. Please retry."); } finally { setBusy(""); } };

  useEffect(() => {
    if (!open) return;
    fetchModels().then((d) => setModels(d.models || [])).catch(() => {});
    fetchSettings().then((d) => { setKeys(d.keys || {}); setPreferred(d.preferred_model || "claude"); }).catch(() => {});
  }, [open]);

  const choosePreferred = async (m) => {
    const previous = preferred;
    setPreferred(m);
    onModelChange?.(m);
    try { await setPreferredModel(m); toast.success("Default model saved"); }
    catch (e) {
      setPreferred(previous);
      onModelChange?.(previous);
      toast.error(e.response?.data?.detail || "Couldn't save default");
    }
  };

  const handleSaveKey = async (provider) => {
    const key = inputs[provider]?.trim();
    if (!key) return;
    setBusy(provider);
    try {
      const res = await saveApiKey(provider, key);
      setKeys(res.keys);
      setInputs((s) => ({ ...s, [provider]: "" }));
      toast.success("Your key is saved — we'll use it for that provider");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save key");
    } finally { setBusy(""); }
  };

  const handleRemoveKey = async (provider) => {
    setBusy(provider);
    try {
      const res = await removeApiKey(provider);
      setKeys(res.keys);
      if (res.preferred_model && res.preferred_model !== preferred) { setPreferred(res.preferred_model); onModelChange?.(res.preferred_model); }
      toast.success(provider === "openrouter" ? "OpenRouter key removed" : "Key removed — back to the shared key");
    } catch { toast.error("Couldn't remove key"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white p-0 gap-0">
        <DialogHeader className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-4">
          <DialogTitle className="font-heading font-bold text-xl text-navy tracking-tight flex items-center gap-2">
            <Cpu size={20} className="text-emerald" /> AI settings
          </DialogTitle>
          <DialogDescription className="text-sm text-navy/60 mt-1">Choose your default model and manage your provider API keys.</DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-7">
          <section data-testid="ai-sharing-settings"><h4 className="font-heading font-semibold text-navy">AI sharing for {siteId || 'the selected site'}</h4><p className="text-sm mt-2">Reports remain available without AI sharing. Ask a question or generate insights to grant permission again. A request already sent cannot be recalled. Withdrawal does not delete earlier conversations; contact hello@seo-signals.com for deletion requests.</p>{isDemo ? <p>Demo explanations send no live data to AI providers.</p> : consentError ? <p role="alert">Permissions could not be loaded. Please reopen Settings to retry.</p> : consent ? <div>{consent.grants?.length ? consent.grants.map(grant => <div key={grant.provider} className="mt-3 border p-3 rounded-md"><p>{grant.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} · {grant.status}</p><p className="text-xs">Version: {consent.version} · Granted: {grant.granted_at || 'Unavailable'}{grant.withdrawn_at && ` · Withdrawn: ${grant.withdrawn_at}`}</p>{grant.status === 'granted' && <button disabled={Boolean(busy)} data-testid={`ai-withdraw-${grant.provider}`} onClick={() => withdraw(grant.provider)} className="mt-2 underline">Withdraw AI sharing</button>}</div>) : <p className="mt-2">No AI sharing permissions granted.</p>}</div> : siteId ? <p>Loading permissions…</p> : <p>Select a site to view permissions.</p>}</section>
          {/* Preferred model */}
          <div>
            <h4 className="font-heading font-semibold text-navy">Default model</h4>
            <p className="text-sm text-navy/55 mt-0.5">Every site opens with this model selected.</p>
          {/*
            Model selection lives here now, not in the dashboard toolbar. The
            toolbar showed it as a two-option segmented control competing with
            the date range for attention, on a setting most people change once.

            `model-switcher` and `model-option-*` moved across with it, so any
            QA script that drove the old toolbar control still resolves. The
            wrapper carries the moved id and the button keeps its original
            `settings-model-*`, because both are in use.
          */}
          <div data-testid="model-switcher" className="mt-3 grid gap-3 sm:grid-cols-2">
              {models.filter((m) => !m.own_key).map((m) => {
                const HIcon = Cpu;
                const active = preferred === m.key;
                return (
                  <div key={m.key} data-testid={`model-option-${m.key}`} className="flex">
                  <button
                    onClick={() => choosePreferred(m.key)}
                    data-testid={`settings-model-${m.key}`}
                    aria-pressed={active}
                    className={`w-full rounded-card border p-4 text-left transition-colors duration-control ease-editorial focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 ${active ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600/30" : "border-line hover:border-line-strong"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-heading font-semibold text-navy-950">{m.key === "claude" ? "Claude Sonnet 5.5" : m.key === "gpt" ? "GPT-6.1 Sol" : m.label}</span>
                      {active && <Check size={16} className="text-emerald-700" aria-hidden="true" />}
                    </div>
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-offwhite px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                      <HIcon size={12} aria-hidden="true" /> {m.provider === "openai" || m.key === "gpt" ? "OpenAI" : m.key === "claude" ? "Anthropic" : "Provider model"}
                    </span>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600">{"Explains the selected site’s connected reports with your permission."}</p>
                  </button>
                  </div>
                );
              })}
            </div>
            {models.some((m) => m.own_key) && (
              <div className="mt-5" data-testid="openrouter-models">
                <h5 className="font-heading font-semibold text-navy text-sm">OpenRouter sharing unavailable</h5>
                <p className="text-sm text-navy/55 mt-0.5">The actual serving recipient has not been verified. Google report data cannot be shared through these routes.</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {models.filter((m) => m.own_key).map((m) => {
                    const active = preferred === m.key;
                    const locked = true;
                    return (
                      <button
                        key={m.key}
                        onClick={() => choosePreferred(m.key)}
                        disabled={locked}
                        data-testid={`settings-model-${m.key}`}
                        aria-pressed={active}
                        className={`w-full rounded-card border px-4 py-3 text-left transition-colors duration-control ease-editorial focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${active ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600/30" : "border-line hover:border-line-strong"}`}
                      >
                        <span className="flex items-center justify-between font-heading font-semibold text-sm text-navy-950">{m.key === "claude" ? "Claude Sonnet 5.5" : m.key === "gpt" ? "GPT-6.1 Sol" : m.label}{active && <Check size={16} className="text-emerald-700" aria-hidden="true" />}</span>
                        <span className="mt-1 block text-xs text-slate-600">{m.provider === "openai" || m.key === "gpt" ? "OpenAI" : m.key === "claude" ? "Anthropic" : "Provider model"}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* BYOK */}
          <div>
            <h4 className="font-heading font-semibold text-navy flex items-center gap-2"><KeyRound size={16} className="text-emerald" /> Bring your own key</h4>
            <p className="text-sm text-navy/55 mt-0.5">During early access, AI answers require your own OpenAI or Anthropic key. Usage is billed by your provider. OpenRouter sharing is currently unavailable.</p>
            <div className="mt-3 space-y-3">
              {PROVIDERS.map((p) => {
                const info = keys[p.id] || {};
                return (
                  <div key={p.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-navy text-sm">{p.label}</span>
                      {info.set ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-dark"><ShieldCheck size={13} /> Using your key ····{info.last4}</span>
                      ) : (
                        <span className="text-xs text-navy/45">{"No key added"}</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="password"
                        value={inputs[p.id]}
                        onChange={(e) => setInputs((s) => ({ ...s, [p.id]: e.target.value }))}
                        placeholder={info.set ? "Replace key…" : p.placeholder}
                        data-testid={`key-input-${p.id}`}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy placeholder:text-navy/35 focus:outline-none focus:ring-2 focus:ring-emerald/25 font-mono"
                      />
                      <button
                        onClick={() => handleSaveKey(p.id)}
                        disabled={busy === p.id || !inputs[p.id]?.trim()}
                        data-testid={`key-save-${p.id}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-dark transition-colors disabled:opacity-50"
                      >
                        {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                      </button>
                      {info.set && (
                        <button
                          onClick={() => handleRemoveKey(p.id)}
                          disabled={busy === p.id}
                          data-testid={`key-remove-${p.id}`}
                          className="grid place-items-center h-9 w-9 rounded-md border border-slate-200 text-navy/50 hover:text-amber-dark hover:border-amber/40 transition-colors"
                          aria-label="Remove key"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <a href={p.link} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-block text-xs font-semibold text-emerald-dark hover:underline">Where do I find this?</a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
